import React from 'react';

const LAST_UPDATED = 'January 21, 2026';

const LegalLayout: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="max-w-3xl mx-auto px-4 py-12">
    <h1 className="text-3xl font-bold text-text mb-3">{title}</h1>
    <p className="text-sm text-muted mb-8">Last updated: {LAST_UPDATED}</p>
    <div className="legal-content text-left">
      {children}
    </div>
  </div>
);

export const PrivacyPolicy: React.FC = () => (
  <LegalLayout title="Privacy Policy">
    <h2>1. Controller</h2>
    <p>
      The Boring Cat<br />
      Owner: George de la Cruz<br />
      Eiffestrasse 600, 20537 Hamburg, Germany<br />
      Email: contact@20minutesrule.com
    </p>

    <h2>2. General information</h2>
    <p>
      We process personal data only as necessary to provide our website and the 20 Minutes Rule app, communicate with you,
      and comply with legal obligations. We follow the GDPR and the German Federal Data Protection Act.
    </p>

    <h2>3. Legal bases</h2>
    <p>
      Article 6(1)(a) GDPR, consent<br />
      Article 6(1)(b) GDPR, contract or pre contract steps<br />
      Article 6(1)(c) GDPR, legal obligation<br />
      Article 6(1)(f) GDPR, legitimate interest
    </p>

    <h2>4. Your rights</h2>
    <p>
      Access, rectification, and deletion of your data<br />
      Restriction of processing and data portability<br />
      Objection to processing and withdrawal of consent<br />
      Complaint to a data protection authority
    </p>

    <h2>5. Access data and server logs</h2>
    <p>
      When you visit our website, we collect technical access data such as IP address, date and time, browser type,
      operating system, and referrer URL. This data is processed to keep the service secure and stable.
    </p>
    <p>Legal basis: Article 6(1)(f) GDPR. Retention period: up to 14 days.</p>

    <h2>6. Hosting and content delivery</h2>
    <p>
      We use Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA, as our hosting and content delivery provider.
      Cloudflare processes technical access data for security and performance. Transfers to the USA rely on the EU Standard
      Contractual Clauses.
    </p>
    <p>More information: https://www.cloudflare.com/privacypolicy/</p>

    <h2>7. Contact and support</h2>
    <p>
      When you contact us by email or through our contact form, we process your name, email address, and message to handle
      your inquiry.
    </p>
    <p>Legal basis: Article 6(1)(b) and Article 6(1)(f) GDPR.</p>

    <h2>8. Email delivery</h2>
    <p>
      We use Resend, Inc. for transactional email delivery. Email addresses and message metadata are processed to deliver
      login links and account confirmations.
    </p>
    <p>Legal basis: Article 6(1)(b) and Article 6(1)(f) GDPR.</p>

    <h2>9. Cookies and local storage</h2>
    <p>
      We use technically necessary cookies to provide login and security features. Optional cookies for analytics or
      marketing are used only with your consent.
    </p>

    <h2>10. Retention and deletion</h2>
    <p>
      Personal data is deleted once the purpose no longer applies, unless legal retention obligations require longer
      storage.
    </p>

    <h2>11. Data security</h2>
    <p>
      We implement technical and organizational measures to protect personal data against loss, manipulation, and
      unauthorized access.
    </p>

    <h2>12. Changes</h2>
    <p>We may update this privacy policy to reflect legal or technical changes.</p>
  </LegalLayout>
);

export const Terms: React.FC = () => (
  <LegalLayout title="Terms & Conditions">
    <h2>1. Scope</h2>
    <p>
      These terms apply to the 20 Minutes Rule web app and related digital services operated by The Boring Cat. They apply
      to both consumers and businesses unless agreed otherwise.
    </p>

    <h2>2. Subject of the contract</h2>
    <p>
      We provide access to our SaaS via the internet. The current feature set is described on our website. No specific
      business outcome is owed.
    </p>

    <h2>3. Contract conclusion</h2>
    <p>The contract is concluded when you register or first use the app.</p>

    <h2>4. User account</h2>
    <p>
      You must provide accurate information, keep access data confidential, and avoid misuse. We may block accounts for
      violations.
    </p>

    <h2>5. Availability</h2>
    <p>
      We aim for high availability but do not guarantee uninterrupted service. Maintenance or incidents may cause
      temporary downtime.
    </p>

    <h2>6. Usage rights</h2>
    <p>
      You receive a non transferable right to use the app for its intended purpose. You may not resell or reverse engineer
      the service.
    </p>

    <h2>7. Compensation</h2>
    <p>The service is currently offered free of charge. We may introduce paid plans in the future with prior notice.</p>
    <p>Small business regulation applies, no VAT is charged under Section 19 UStG.</p>

    <h2>8. Term and termination</h2>
    <p>You may delete your account at any time. We may terminate accounts in case of misuse or legal obligations.</p>

    <h2>9. User obligations</h2>
    <p>You must not use the service for unlawful purposes or to store illegal content.</p>

    <h2>10. Warranty</h2>
    <p>The service is provided according to current technical standards. Statutory warranty rights apply for consumers.</p>

    <h2>11. Liability</h2>
    <p>
      We are liable for intent and gross negligence. For minor negligence, liability is limited to foreseeable damages.
      Liability for injury to life, body, or health remains unlimited.
    </p>

    <h2>12. Data protection</h2>
    <p>Personal data is processed according to our privacy policy.</p>

    <h2>13. Governing law</h2>
    <p>German law applies. Consumer protections remain unaffected.</p>
  </LegalLayout>
);

export const Imprint: React.FC = () => (
  <LegalLayout title="Imprint">
    <h2>Information pursuant to Section 5 TMG</h2>
    <p>
      The Boring Cat<br />
      Owner: George de la Cruz<br />
      Legal form: Sole proprietorship
    </p>

    <h2>Address</h2>
    <p>Eiffestrasse 600, 20537 Hamburg, Germany</p>

    <h2>Email</h2>
    <p>contact@20minutesrule.com</p>

    <h2>Business activities</h2>
    <p>
      Digital products, SaaS applications, and online services<br />
      Software development, support, and maintenance
    </p>

    <h2>VAT</h2>
    <p>In accordance with Section 19 UStG, no VAT is charged.</p>

    <h2>Responsible for content</h2>
    <p>George de la Cruz, address as above.</p>
  </LegalLayout>
);

export const CookiePolicy: React.FC = () => (
  <LegalLayout title="Cookie Policy">
    <h2>1. General information</h2>
    <p>
      This cookie policy explains how The Boring Cat uses cookies and similar technologies on 20 Minutes Rule.
    </p>

    <h2>2. What are cookies?</h2>
    <p>
      Cookies are small text files stored on your device. We also use similar technologies such as local storage.
    </p>

    <h2>3. Categories of cookies</h2>
    <p>
      Technically necessary cookies for security, login, and core functionality.<br />
      Analytics and statistics cookies only with your consent.<br />
      Marketing and third party cookies only with your consent.
    </p>

    <h2>4. Services used</h2>
    <p>
      Cloudflare for security and performance, technically necessary cookies only.<br />
      Resend for email delivery, no marketing cookies.
    </p>

    <h2>5. Consent and settings</h2>
    <p>
      On your first visit, you can choose your preferences in the cookie banner. You can update your choices at any time in
      <a href="/cookie-settings">Cookie Settings</a>.
    </p>

    <h2>6. Browser controls</h2>
    <p>You can delete or block cookies in your browser settings. This may limit site functionality.</p>

    <h2>7. Changes</h2>
    <p>We may update this cookie policy as our services change.</p>
  </LegalLayout>
);

export const Accessibility: React.FC = () => (
  <LegalLayout title="Accessibility Statement">
    <p>
      The Boring Cat is committed to making its websites and digital services as accessible as possible.
    </p>
    <h2>Current accessibility status</h2>
    <p>We continuously improve accessibility, although some content may not yet fully comply with all requirements.</p>
    <h2>Measures to support accessibility</h2>
    <p>
      Clear and consistent navigation<br />
      Readable text with sufficient contrast<br />
      Keyboard accessibility where reasonably possible<br />
      Responsive layouts for different devices
    </p>
    <h2>Feedback</h2>
    <p>If you experience accessibility issues, contact us at contact@20minutesrule.com.</p>
  </LegalLayout>
);
